import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Page from '../../components/Page';
import LoadingSpinner from '../../components/LoadingSpinner';
import StyledMainGrid from '../../components/StyledMainGrid';
import { ajaxGetDraftPurchaseOrders, ajaxGetWaitingApprovalPurchaseOrders, ajaxGetPurchaseOrdersByTime } from '../../services/purchaseOrderService';
import { ajaxGetSuppliersAutocomplete } from '../../services/supplierService';
import DraftPOList from './DraftPOList';
import DraftPOCount from './DraftPOCount';
import WaitingApprovalPOList from './WaitingApprovalPOList';
import WaitingApprovalPOCount from './WaitingApprovalPOCount';
import RecentPOList from './RecentPOList';

export default function PurchasingDashboard() {
  const navigate = useNavigate();
  const incomingParams = useParams();
  const [supplierId, setSupplierId] = useState(Number(incomingParams.supplierId) ? Number(incomingParams.supplierId) : -1);
  const [autocomplete, setAutocomplete] = useState(null);
  const [displayInfo, setDisplayInfo] = useState(false);

  useEffect(() => {
    setDisplayInfo(false);
    setSupplierId(Number(incomingParams.supplierId) ? Number(incomingParams.supplierId) : -1);
  }, [incomingParams.supplierId]);

  useEffect(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    ajaxGetSuppliersAutocomplete().then((supplierRes) => {
      Promise.all([
        ajaxGetDraftPurchaseOrders(),
        ajaxGetWaitingApprovalPurchaseOrders(),
        ajaxGetPurchaseOrdersByTime(Math.floor(thirtyDaysAgo.getTime() / 1000))
      ]).then(([draftRes, waitingApprovalRes, recentRes]) => {
        const supplierList = supplierRes.data;
        const draftOrders = draftRes.data;
        const waitingApprovalOrders = waitingApprovalRes.data;
        const recentOrders = recentRes.data;
        const autocompleteList = [
          { id: -1, name: 'All Suppliers' },
          ...supplierList.map(s => ({ id: s.id, name: s.name }))
        ]
        const info = {
          autocompleteList: [...autocompleteList],
          draftOrders: (supplierId === -1 ? [...draftOrders] : draftOrders.filter(o => o.supplier === supplierId)),
          waitingApprovalOrders: (supplierId === -1 ? [...waitingApprovalOrders] : waitingApprovalOrders.filter(o => o.supplier === supplierId)),
          recentOrders: (supplierId === -1 ? [...recentOrders] : recentOrders.filter(o => o.supplier === supplierId)),
          suppliers: [...supplierList]
        };

        setDisplayInfo(info);
        setAutocomplete(autocompleteList.find(s => s.id === supplierId));
      })
      .catch(() => {
        setDisplayInfo(false);
        setAutocomplete(null);
      });
    }).catch(() => {
      setDisplayInfo(false);
      setAutocomplete(null);
    });
  }, [supplierId]);

  const handleSupplierNameSearch = (event, value) => {
    setAutocomplete(value);
    if (value) {
      navigate(`/dashboard/${value.id}`);
    }
  };

  return (
    <Page title="Purchasing Overview">
      <StyledMainGrid columnSpacing alignItems="flex-start" sx={{ pb: '40px' }}>
        <Grid item xs={12}>
          <Autocomplete
            disablePortal
            sx={{ width: '100%' }}
            size="small"
            value={autocomplete}
            options={displayInfo ? displayInfo.autocompleteList : []}
            isOptionEqualToValue={(option, value) => (value && option.id === value.id)}
            getOptionLabel={(option) => `${option.name} [${option.id}]`}
            onChange={handleSupplierNameSearch}
            renderInput={(params) => <TextField {...params} label="Supplier Name" />}
          />
        </Grid>
        {
          !displayInfo &&
          <Grid container item xs={12} sx={{ position: 'relative', height: '50vh' }}>
            <LoadingSpinner />
          </Grid>
        }
        {
          displayInfo && 
          <Grid container item xs={12} rowSpacing="22px" columnSpacing="22px">
            <Grid item xs={12} sm={8}>
              <DraftPOList purchaseOrders={displayInfo.draftOrders} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DraftPOCount purchaseOrders={displayInfo.draftOrders} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <WaitingApprovalPOList purchaseOrders={displayInfo.waitingApprovalOrders} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <WaitingApprovalPOCount purchaseOrders={displayInfo.waitingApprovalOrders} />
            </Grid>
            <Grid item xs={12}>
              <RecentPOList purchaseOrders={displayInfo.recentOrders} />
            </Grid>
          </Grid>
        }
      </StyledMainGrid>
    </Page>
  );
}
